import React, { useState, useEffect, useRef } from 'react';
import { useContent } from '../../hooks/useContent';
import { apiUpload } from '../../api/client';

const F = "'Inter', system-ui, sans-serif";

function generateId() {
  return 'x' + Math.random().toString(36).substr(2, 8);
}

const EMPTY_CAFE = { heading: '', subheading: '', intro: '', intro2: '', seasonNote: '', image: '' };
const EMPTY_LEISTUNG = { id: '', icon: '🏕️', title: '', subtitle: '', description: '', capacity: '', photo: '', features: [] };

export default function ContentEditor() {
  const { content, loading, error, saveContent } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [open, setOpen] = useState(new Set(['hero']));

  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => {
    if (content && !draft) {
      const d = JSON.parse(JSON.stringify(content));
      if (!d.cafe) d.cafe = { ...EMPTY_CAFE };
      if (!d.leistungen) d.leistungen = [];
      if (!d.hero.heroImage) d.hero.heroImage = '';
      d.leistungen.forEach(l => { if (!l.photo) l.photo = ''; });
      setDraft(d);
    }
  }, [content, draft]);

  if (loading) return <p style={styles.info}>Lade Inhalte...</p>;
  if (error) return <p style={styles.errorText}>Fehler: {error}</p>;
  if (!draft) return null;

  const set = (path, value) => {
    const keys = path.split('.');
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setSaved(false);
  };

  const setLeistungField = (i, field, value) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.leistungen[i][field] = value; return next; });
    setSaved(false);
  };

  // ── Highlights ───────────────────────────────────────────────
  const updateHighlight = (index, field, value) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.highlights[index][field] = value; return next; });
    setSaved(false);
  };
  const addHighlight = () => {
    setDraft(prev => ({ ...prev, highlights: [...(prev.highlights || []), { id: generateId(), icon: '⭐', title: 'Neu', text: '' }] }));
    setSaved(false);
  };
  const removeHighlight = (index) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.highlights.splice(index, 1); return next; });
    setSaved(false);
  };

  // ── Leistungen ────────────────────────────────────────────────
  const addLeistung = () => {
    setDraft(prev => ({ ...prev, leistungen: [...(prev.leistungen || []), { ...EMPTY_LEISTUNG, id: generateId() }] }));
    setSaved(false);
  };
  const removeLeistung = (i) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.leistungen.splice(i, 1); return next; });
    setSaved(false);
  };
  const updateLeistungFeature = (i, fi, value) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.leistungen[i].features[fi] = value; return next; });
    setSaved(false);
  };
  const addLeistungFeature = (i) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.leistungen[i].features.push(''); return next; });
    setSaved(false);
  };
  const removeLeistungFeature = (i, fi) => {
    setDraft(prev => { const next = JSON.parse(JSON.stringify(prev)); next.leistungen[i].features.splice(fi, 1); return next; });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveContent(draft);
      setSaved(true);
    } catch (e) {
      alert('Fehler beim Speichern: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.editor}>

      <Accordion id="hero" title="Hero-Bereich" open={open} toggle={toggle}>
        <Field label="Hauptüberschrift" value={draft.hero?.heading || ''} onChange={v => set('hero.heading', v)} />
        <Field label="Untertitel" value={draft.hero?.subheading || ''} onChange={v => set('hero.subheading', v)} />
        <p style={styles.subLabel}>Hintergrundbild</p>
        <ImageUploader
          value={draft.hero?.heroImage || ''}
          onChange={v => set('hero.heroImage', v)}
          previewHeight={160}
        />
      </Accordion>

      <Accordion id="ueberUns" title="Über uns" open={open} toggle={toggle}>
        <Field label="Überschrift" value={draft.ueberUns?.heading || ''} onChange={v => set('ueberUns.heading', v)} />
        <Field label="Text" value={draft.ueberUns?.text || ''} onChange={v => set('ueberUns.text', v)} multiline />
      </Accordion>

      <Accordion id="highlights" title={`Highlights (${(draft.highlights || []).length})`} open={open} toggle={toggle}>
        {(draft.highlights || []).map((h, i) => (
          <div key={h.id} style={styles.rowBlock}>
            <div style={styles.rowFields}>
              <Field label={`Icon ${i + 1}`} value={h.icon} onChange={v => updateHighlight(i, 'icon', v)} small />
              <Field label="Titel" value={h.title} onChange={v => updateHighlight(i, 'title', v)} />
              <Field label="Text" value={h.text} onChange={v => updateHighlight(i, 'text', v)} />
            </div>
            <button style={styles.removeBtn} onClick={() => removeHighlight(i)} title="Entfernen">✕</button>
          </div>
        ))}
        <button style={styles.addBtn} onClick={addHighlight}>+ Highlight hinzufügen</button>
      </Accordion>

      <Accordion id="kontakt" title="Kontakt" open={open} toggle={toggle}>
        <Field label="Name" value={draft.contact?.name || ''} onChange={v => set('contact.name', v)} />
        <Field label="Adresse" value={draft.contact?.address || ''} onChange={v => set('contact.address', v)} />
        <Field label="Telefon" value={draft.contact?.phone || ''} onChange={v => set('contact.phone', v)} />
        <Field label="E-Mail" value={draft.contact?.email || ''} onChange={v => set('contact.email', v)} />
      </Accordion>

      <Accordion id="cafe" title="Café" open={open} toggle={toggle}>
        <Field label="Seitenüberschrift" value={draft.cafe?.heading || ''} onChange={v => set('cafe.heading', v)} />
        <Field label="Untertitel" value={draft.cafe?.subheading || ''} onChange={v => set('cafe.subheading', v)} />
        <Field label="Einleitungstext 1" value={draft.cafe?.intro || ''} onChange={v => set('cafe.intro', v)} multiline />
        <Field label="Einleitungstext 2" value={draft.cafe?.intro2 || ''} onChange={v => set('cafe.intro2', v)} multiline />
        <Field label="Saisonhinweis" value={draft.cafe?.seasonNote || ''} onChange={v => set('cafe.seasonNote', v)} />
        <p style={styles.subLabel}>Foto</p>
        <ImageUploader
          value={draft.cafe?.image || ''}
          onChange={v => set('cafe.image', v)}
          previewHeight={140}
        />
      </Accordion>

      <Accordion id="leistungen" title={`Leistungen (${(draft.leistungen || []).length})`} open={open} toggle={toggle}>
        {(draft.leistungen || []).map((item, i) => (
          <div key={item.id} style={styles.catBlock}>
            <div style={styles.catHeader}>
              <Field label="Icon" value={item.icon} onChange={v => setLeistungField(i, 'icon', v)} small />
              <div style={{ flex: 1 }}>
                <Field label="Titel" value={item.title} onChange={v => setLeistungField(i, 'title', v)} />
              </div>
              <button style={styles.removeCatBtn} onClick={() => removeLeistung(i)}>✕ Entfernen</button>
            </div>
            <Field label="Untertitel" value={item.subtitle} onChange={v => setLeistungField(i, 'subtitle', v)} />
            <Field label="Beschreibung" value={item.description} onChange={v => setLeistungField(i, 'description', v)} multiline />
            <Field label="Kapazität" value={item.capacity} onChange={v => setLeistungField(i, 'capacity', v)} />
            <p style={styles.subLabel}>Foto</p>
            <ImageUploader
              value={item.photo || ''}
              onChange={v => setLeistungField(i, 'photo', v)}
              previewHeight={120}
            />
            <p style={styles.subLabel}>Features / Ausstattung</p>
            {(item.features || []).map((f, fi) => (
              <div key={fi} style={styles.rowBlock}>
                <div style={{ flex: 1 }}>
                  <Field label={`Feature ${fi + 1}`} value={f} onChange={v => updateLeistungFeature(i, fi, v)} />
                </div>
                <button style={styles.removeBtn} onClick={() => removeLeistungFeature(i, fi)} title="Entfernen">✕</button>
              </div>
            ))}
            <button style={styles.addBtnSmall} onClick={() => addLeistungFeature(i)}>+ Feature hinzufügen</button>
          </div>
        ))}
        <button style={styles.addBtn} onClick={addLeistung}>+ Leistung hinzufügen</button>
      </Accordion>

      <div style={styles.saveRow}>
        {saved && <span style={styles.savedNote}>✓ Gespeichert</span>}
        <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Speichern...' : 'Änderungen speichern'}
        </button>
      </div>
    </div>
  );
}

// ── Reusable image uploader ───────────────────────────────────
function ImageUploader({ value, onChange, previewHeight = 160 }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiUpload('/gallery.php', formData);
      onChange(data.url);
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      {value && (
        <img src={value} alt="" style={{ ...uploaderStyles.preview, height: previewHeight + 'px' }} />
      )}
      <div style={uploaderStyles.row}>
        <label style={{ ...uploaderStyles.btn, opacity: uploading ? 0.6 : 1 }}>
          {uploading ? 'Wird hochgeladen...' : '📷 Foto hochladen'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            ref={fileRef}
            onChange={handleFile}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>
        {value && (
          <button style={uploaderStyles.removeBtn} onClick={() => onChange('')}>✕ Entfernen</button>
        )}
      </div>
      <p style={uploaderStyles.hint}>JPG, PNG, WebP · Max. 10 MB</p>
    </div>
  );
}

// ── Accordion ────────────────────────────────────────────────
function Accordion({ id, title, open, toggle, children }) {
  const isOpen = open.has(id);
  return (
    <div style={accStyles.wrap}>
      <button style={accStyles.header} onClick={() => toggle(id)}>
        <span style={accStyles.title}>{title}</span>
        <span style={accStyles.chevron}>{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && <div style={accStyles.body}>{children}</div>}
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────
function Field({ label, value, onChange, multiline, small }) {
  return (
    <div style={fieldStyles.group}>
      <label style={fieldStyles.label}>{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={4} style={fieldStyles.textarea} />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ ...fieldStyles.input, ...(small ? { maxWidth: '80px' } : {}) }}
        />
      )}
    </div>
  );
}

const accStyles = {
  wrap: { border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '0.75rem', backgroundColor: 'white' },
  header: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.9rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: F, fontSize: '0.95rem', fontWeight: '600', color: '#111827', textAlign: 'left',
  },
  title: { flex: 1 },
  chevron: { color: '#9ca3af', fontSize: '0.75rem', marginLeft: '0.5rem' },
  body: { padding: '1.25rem', borderTop: '1px solid #f3f4f6' },
};

const uploaderStyles = {
  preview: { width: '100%', objectFit: 'cover', borderRadius: '8px', marginBottom: '0.75rem', display: 'block' },
  row: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  btn: { backgroundColor: '#f0fdf4', color: '#1e8449', border: '1px solid #86efac', borderRadius: '8px', padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: F, fontSize: '0.875rem', fontWeight: '600' },
  removeBtn: { background: 'none', border: '1px solid #fca5a5', color: '#dc3545', fontSize: '0.78rem', borderRadius: '6px', cursor: 'pointer', padding: '0.3rem 0.6rem', whiteSpace: 'nowrap', fontFamily: F },
  hint: { color: '#9ca3af', fontFamily: F, fontSize: '0.78rem', marginTop: '0.4rem' },
};

const styles = {
  editor: { display: 'flex', flexDirection: 'column' },

  rowBlock: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '8px' },
  rowFields: { flex: 1, display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },

  catBlock: { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '0.85rem', backgroundColor: '#f9fafb' },
  catHeader: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' },

  subLabel: { fontFamily: F, fontSize: '0.8rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '1rem 0 0.6rem' },

  removeBtn: { background: 'none', border: 'none', color: '#dc3545', fontSize: '1.1rem', cursor: 'pointer', padding: '0.3rem', lineHeight: 1, marginTop: '0.3rem', flexShrink: 0 },
  removeCatBtn: { background: 'none', border: '1px solid #fca5a5', color: '#dc3545', fontSize: '0.78rem', borderRadius: '6px', cursor: 'pointer', padding: '0.3rem 0.6rem', alignSelf: 'flex-end', marginBottom: '0.75rem', whiteSpace: 'nowrap', fontFamily: F },
  addBtn: { backgroundColor: '#f0fdf4', color: '#1e8449', border: '1px dashed #86efac', borderRadius: '8px', padding: '0.6rem 1rem', cursor: 'pointer', fontFamily: F, fontSize: '0.875rem', width: '100%', marginTop: '0.25rem' },
  addBtnSmall: { backgroundColor: '#f0fdf4', color: '#1e8449', border: '1px dashed #86efac', borderRadius: '6px', padding: '0.4rem 0.9rem', cursor: 'pointer', fontFamily: F, fontSize: '0.82rem', marginTop: '0.5rem' },

  saveRow: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem', paddingTop: '0.5rem' },
  savedNote: { color: '#1e8449', fontFamily: F, fontSize: '0.9rem', fontWeight: '600' },
  saveBtn: { backgroundColor: '#1e8449', color: 'white', border: 'none', borderRadius: '8px', padding: '0.8rem 2rem', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: F },
  info: { color: '#6b7280', fontFamily: F },
  errorText: { color: '#dc3545', fontFamily: F },
};

const fieldStyles = {
  group: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem', flex: 1, minWidth: '160px' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', fontFamily: F },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem', fontFamily: F, backgroundColor: 'white' },
  textarea: { padding: '0.55rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem', fontFamily: F, resize: 'vertical', backgroundColor: 'white' },
};
